import React, { useRef, useState } from 'react';
import { Camera, X, Image as ImageIcon } from 'lucide-react';
import { JobPhoto, PhotoStage } from '../../types/technicianJob';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface PhotoCaptureProps {
  photos: JobPhoto[];
  onCapture: (photo: Omit<JobPhoto, 'id'>) => void;
  onRemove: (id: string) => void;
  readonly?: boolean;
}

const STAGE_LABELS: Record<PhotoStage, string> = {
  before: 'Before Work',
  in_progress: 'In Progress',
  after: 'After Completion',
};

const STAGE_VARIANTS: Record<PhotoStage, 'info' | 'warning' | 'success'> = {
  before: 'info',
  in_progress: 'warning',
  after: 'success',
};

export const PhotoCapture: React.FC<PhotoCaptureProps> = ({ photos, onCapture, onRemove, readonly }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStage, setSelectedStage] = useState<PhotoStage>('before');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      onCapture({
        stage: selectedStage,
        data_url: reader.result as string,
        captured_at: new Date().toISOString(),
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const grouped = photos.reduce<Record<PhotoStage, JobPhoto[]>>((acc, p) => {
    acc[p.stage].push(p);
    return acc;
  }, { before: [], in_progress: [], after: [] });

  return (
    <div className="space-y-3">
      {!readonly && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedStage}
            onChange={e => setSelectedStage(e.target.value as PhotoStage)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:ring-2 focus:ring-sky-500"
          >
            <option value="before">Before Work</option>
            <option value="in_progress">In Progress</option>
            <option value="after">After Completion</option>
          </select>
          <Button size="sm" variant="outline" icon={<Camera className="w-3.5 h-3.5" />} onClick={() => fileInputRef.current?.click()}>
            Take Photo
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
        </div>
      )}

      {(['before', 'in_progress', 'after'] as PhotoStage[]).map(stage => (
        grouped[stage].length > 0 && (
          <div key={stage}>
            <p className="text-[10px] text-slate-500 font-medium mb-1.5">{STAGE_LABELS[stage]}</p>
            <div className="flex flex-wrap gap-2">
              {grouped[stage].map(photo => (
                <div key={photo.id} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
                  {photo.data_url ? (
                    <img src={photo.data_url} alt={photo.notes || 'Photo'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-slate-600" />
                    </div>
                  )}
                  {!readonly && (
                    <button
                      onClick={() => onRemove(photo.id)}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-rose-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-900/80 px-1 py-0.5">
                    <Badge label={STAGE_LABELS[photo.stage]} variant={STAGE_VARIANTS[photo.stage]} className="text-[8px] px-1 py-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {photos.length === 0 && (
        <p className="text-xs text-slate-500 italic">No photos captured yet</p>
      )}
    </div>
  );
};
